<template>
  <div class="form-group position-relative">
    <label class="form-label"
           :class="formLabelClass"
           v-if="showlabel && label">
      <span>{{ label }}</span>
      <span v-if="required"> *</span>
    </label>
    <div class="position-relative"
         v-if="editMode">
      <input v-if="multiple"
             class="form-control bg-white overflow-ellipsis"
             :value="valueText"
             @focus="focus"
             readonly
      />
      <SpaceduckMaskInput v-else
                 :model-value="contentStringMasked"
                 @update:model-value="contentStringMasked = $event"
                 @keyup="manualInput"
                 @focus="focus"
                 :readonly="readonly"
                 :placeholder="placeholder"
                 class="form-control"
                 mask="##.##.####" />
      <CalendarSvg class="position-absolute calendar-icon non-clickable"
                   v-if="calendarIcon && !readonly"/>
      <TrashSvg class="position-absolute calendar-icon svg-gray pointer"
                v-if="clearable && contentStringMasked"
                @click="clear"/>
    </div>
    <div v-if="show"
         v-click-outside="clickedOutside"
         class="date-picker"
         :class="{ labeled: !!label }">
      <VDatePicker :locale="{
                      id: lang,
                      firstDayOfWeek: 2,
                      masks: { L: 'DD.MM.YYYY' }
                   }"
                   :readonly="readonly"
                   @update:model-value="handleInput"
                   @dayclick="dayClicked"
                   :min-date="minDate"
                   :max-date="maxDate"
                   v-model="content"
                   :attributes='attrs'/>
    </div>
    <div v-if="!editMode">{{ contentStringMasked || '-' }}</div>
    <ValidationError class="mt-1"
                     v-if="showErrors"
                     :validationId="validationId"/>
  </div>
</template>

<script setup>
import moment from 'moment'
import { DatePicker as VDatePicker } from 'v-calendar'
import ValidationError from "@/components/form/ValidationError"
import CalendarSvg from '@/assets/svg-vue/calendar/calendar.svg'
import TrashSvg from '@/assets/svg-vue/general/trash_tr.svg'
import SpaceduckMaskInput from "@/components/parts/general/SpaceduckMaskInput.vue"
import {computed, getCurrentInstance, nextTick, ref, watch} from "vue"

const emit = defineEmits(['update:modelValue', 'dayClicked'])

const props = defineProps({
  label: {},
  placeholder: {
    type: String,
    default: '__.__.____',
  },
  modelValue: {},
  showErrors: {
    type: Boolean,
    default: false
  },
  closeOnClick: {
    type: Boolean,
    default: true
  },
  clearable: {
    type: Boolean,
    default: false
  },
  attrs: {
    type: Array,
    default: () => [{}]
  },
  showlabel: {
    type: Boolean,
    default: true
  },
  calendarIcon: {
    type: Boolean,
    default: true
  },
  readonly: {
    type: Boolean,
    default: false
  },
  validationId: {},
  required: {
    type: Boolean,
    default: false
  },
  multiple: {
    type: Boolean,
    default: false
  },
  valueText: {
    type: String,
    default: null
  },
  minDate: {
    type: String,
    default: null
  },
  maxDate: {
    type: String,
    default: null
  },
  formLabelClass: {
    type: String,
  },
  editMode: {
    type: Boolean,
    default: true
  }
})

const { proxy } = getCurrentInstance()

const show = ref(false)
const content = ref(new Date(moment(props.modelValue)))
const contentString = ref(props.modelValue)
const contentStringMasked = ref(props.modelValue ? moment(props.modelValue).format('DD.MM.YYYY') : null)
let timer = null

const lang = computed(() => {
  const supportedLangs = ['ru', 'en', 'uk']
  return supportedLangs.includes(proxy.$i18n.locale) ? proxy.$i18n.locale : 'ru'
})

const handleInput = (e) => {
  const val = moment(e).format('YYYY-MM-DD')
  contentString.value = val
  if(props.closeOnClick) show.value = false
}

const dayClicked = (e, event) => {
  event?.target?.blur()
  emit('dayClicked', e.id)
  handleInput(e.id)
}

const focus = () => {
  if(props.readonly) return
  show.value = true
  timer = setTimeout(() => {
    timer = null
  }, 300)
}

const clickedOutside = () => {
  if(timer) return
  show.value = false
}

const manualInput = () => {
  if(!contentStringMasked.value || contentStringMasked.value.length === 0) {
    contentString.value = null
    return
  }
  if(contentStringMasked.value.length === 10) {
    contentString.value = moment(contentStringMasked.value, 'DD.MM.YYYY').format('YYYY-MM-DD')
    show.value = false
  }
  if(contentStringMasked.value === 'Invalid date') contentStringMasked.value = null
}

const refresh = () => {
  contentString.value = props.modelValue
  contentStringMasked.value = props.modelValue ? moment(props.modelValue).format('DD.MM.YYYY') : null
}

const clear = () => {
  contentString.value = ''
}

watch(contentString, async (val) => {
  emit('update:modelValue', val === 'Invalid date' ? null : val)
  content.value = new Date(moment(val))
  contentStringMasked.value = moment(val).format('DD.MM.YYYY')
  if(props.minDate && val < props.minDate) {
    contentString.value = props.minDate
    await nextTick()
    refresh()
  }
})

watch(() => props.modelValue, (val) => {
  if(val != contentString.value) refresh()
})
</script>

<style scoped lang="scss">
.date-picker {
  position: absolute;
  top: 45px;
  left: 0;
  &.labeled {
    top: 73px;
  }
}
.right-popup {
  .date-picker {
    left: auto;
    right: 0;
  }
}
.calendar-icon {
  top: 50%;
  right: 15px;
  width: 16px;
  height: 16px;
  margin-top: -8px;
  &.non-clickable {
    pointer-events: none;
  }
}
.overflow-ellipsis {
  padding-right: 50px;
}
@media screen and (max-width: 768px) {
  .calendar-icon {
    right: 13px;
    width: 12px;
    height: 12px;
    margin-top: -6px;
  }
  .date-picker {
    top: 31px;
    &.labeled {
      top: 51px;
    }
  }
}
</style>
